import React from 'react';
import {
  Award, Calendar, Target, ShieldCheck, CheckCircle2,
  FileText, Activity, AlertCircle, HeartHandshake, User, Phone, Printer
} from 'lucide-react';
import { ClinicalDocument, EvalSectionData, PocSectionData, QprSectionData } from '../../types';
import { continuedSkilledNeedDisplay, goalTermLabel, isAuthoredEvalDoc, isAuthoredPlanDoc, isFeedingEvalDoc, isFeedingPlanDoc } from '../../utils/clinicalDocDisplay';
import { qprAbsentText, qprGoalReviewPresentation } from '../../utils/clinicalAuthoringClient';

interface ClinicalDocStructuredDisplayProps {
  doc: ClinicalDocument;
  sectionData: any;
  lightTheme?: boolean;
}

export const ClinicalDocStructuredDisplay: React.FC<ClinicalDocStructuredDisplayProps> = ({
  doc,
  sectionData,
  lightTheme = false
}) => {
  if (!sectionData) return null;

  if (isAuthoredEvalDoc(doc.doc_type)) {
    const data = sectionData as EvalSectionData;
    const dischargeCriteria = String(data.discharge_criteria || '').trim();
    const medicalNecessityRationale = String(data.medical_necessity_rationale || '').trim();
    const caregiverConcerns = String(data.caregiver_concerns || '').trim();
    const safetyPrecautions = String(data.safety_precautions || '').trim();
    const medicalHistory = String(data.medical_history || '').trim();
    const caregiverTrainingPlan = String(data.caregiver_training_plan || '').trim();
    const homeProgramStrategies = String(data.home_program_strategies || '').trim();
    const diagnosticImpressions = String(data.diagnostic_impressions || '').trim();
    const receptiveLanguage = String(data.receptive_language || '').trim();
    const expressiveLanguage = String(data.expressive_language || '').trim();
    const speechProduction = String(data.speech_production || '').trim();
    const pragmaticSocial = String(data.pragmatic_social || '').trim();
    return (
      <div className={`space-y-4 text-xs ${lightTheme ? 'clinical-structured-light text-slate-800' : ''}`}>
        {/* EVAL Header Highlights */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#1c2c47] via-[#162339] to-[#121c2e] border border-slate-600/70 shadow-[0_4px_16px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)] grid grid-cols-2 sm:grid-cols-4 gap-3 text-slate-200">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400 block">Prescribed Frequency</span>
            <span className="font-bold text-white">{data.prescribed_frequency || '2x/week'}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400 block">Session Duration</span>
            <span className="font-bold text-white">{data.prescribed_duration || '60 minutes'} ({isFeedingEvalDoc(doc.doc_type) ? '92526' : (data.cpt_code || '92507')})</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400 block">Initial POC Window</span>
            <span className="font-mono text-white font-bold">{data.poc_start_date || 'Active'} to {data.poc_end_date || '12 Months'}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400 block">Prognosis</span>
            <span className="font-bold text-emerald-400">{data.prognosis || 'Good'}</span>
          </div>
        </div>
        {(data.evaluation_date || data.eval_cpt_code) && (
          <p className="text-[11px] text-slate-300">
            {data.evaluation_date ? `Evaluation date: ${data.evaluation_date}` : ''}
            {data.time_in && data.time_out ? ` ${data.time_in}-${data.time_out}` : ''}
            {isFeedingEvalDoc(doc.doc_type) ? ' · Evaluation CPT 92610' : (data.eval_cpt_code ? ` · Evaluation CPT ${data.eval_cpt_code}` : '')}
          </p>
        )}

        {/* Section 1: Client Context & Background */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <User className="w-3.5 h-3.5 text-sky-400" />
            <span>1. Client Context, Safety Precautions & Medical History</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200 leading-relaxed">
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="text-sky-400 block text-[10px] uppercase font-bold tracking-wider mb-1">Clinical Context:</strong>
              <p className="text-slate-100 font-normal">{data.client_context || 'Not specified'}</p>
            </div>
            {caregiverConcerns ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="text-sky-400 block text-[10px] uppercase font-bold tracking-wider mb-1">Caregiver Concerns:</strong>
              <p className="text-slate-100 font-normal">{caregiverConcerns}</p>
            </div>
            ) : null}
            {medicalHistory ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="text-sky-400 block text-[10px] uppercase font-bold tracking-wider mb-1">Medical History & Hearing Status:</strong>
              <p className="text-slate-100 font-normal">{medicalHistory}</p>
            </div>
            ) : null}
            {safetyPrecautions ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="text-sky-400 block text-[10px] uppercase font-bold tracking-wider mb-1">Safety Precautions & Physical Contraindications:</strong>
              <p className="text-slate-100 font-normal">{safetyPrecautions}</p>
            </div>
            ) : null}
          </div>
        </div>

        {/* Section 2: Standardized Assessment Scores */}
        {data.standardized_tests && data.standardized_tests.length > 0 && (
          <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
            <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
              <Award className="w-3.5 h-3.5 text-sky-400" />
              <span>2. Standardized Assessment Instrument Scores</span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-700/80">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#0a101b] text-slate-300 text-[10px] uppercase font-bold border-b border-slate-700/80">
                    <th className="p-2.5">Instrument Name</th>
                    <th className="p-2.5">Date</th>
                    <th className="p-2.5">Standard Score</th>
                    <th className="p-2.5">Percentile</th>
                    <th className="p-2.5">Interpretation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80 bg-[#070c14]">
                  {data.standardized_tests.map((test, idx) => (
                    <tr key={idx} className="hover:bg-slate-800/40 transition-colors">
                      <td className="p-2.5 font-bold text-white">{test.test_name}</td>
                      <td className="p-2.5 text-slate-400 font-mono">{test.date_administered || 'Date of Eval'}</td>
                      <td className="p-2.5 font-mono font-bold text-sky-400">{test.standard_score ?? 'N/A'}</td>
                      <td className="p-2.5 font-mono text-slate-300">{test.percentile ? `${test.percentile}%ile` : 'N/A'}</td>
                      <td className="p-2.5 text-slate-300 leading-normal">{test.interpretation || 'Completed'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Section 3: Detailed Speech & Language Findings */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <Activity className="w-3.5 h-3.5 text-sky-400" />
            <span>{isFeedingEvalDoc(doc.doc_type) ? '3. Feeding and Swallowing Findings' : '3. Clinical Findings across Speech & Language Domains'}</span>
          </div>
          {isFeedingEvalDoc(doc.doc_type) ? (
            <p className="text-slate-100 leading-relaxed whitespace-pre-wrap">{data.feeding_findings || 'Not recorded'}</p>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200 leading-relaxed">
            {receptiveLanguage ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-sky-400 font-bold tracking-wider mb-1">Receptive Language & Auditory Comprehension</strong>
              <p className="text-slate-100 font-normal">{receptiveLanguage}</p>
            </div>
            ) : null}
            {expressiveLanguage ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-sky-400 font-bold tracking-wider mb-1">Expressive Language & Syntax</strong>
              <p className="text-slate-100 font-normal">{expressiveLanguage}</p>
            </div>
            ) : null}
            {speechProduction ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-sky-400 font-bold tracking-wider mb-1">Speech Sound Production & Phonology</strong>
              <p className="text-slate-100 font-normal">{speechProduction}</p>
            </div>
            ) : null}
            {pragmaticSocial ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-sky-400 font-bold tracking-wider mb-1">Pragmatic & Social Communication</strong>
              <p className="text-slate-100 font-normal">{pragmaticSocial}</p>
            </div>
            ) : null}
            {data.aac_multimodal && (
              <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs md:col-span-2">
                <strong className="block text-[10px] uppercase text-sky-400 font-bold tracking-wider mb-1">AAC & Multi-Modal Communication</strong>
                <p className="text-slate-100 font-normal">{data.aac_multimodal}</p>
              </div>
            )}
            {data.oral_motor_voice && (
              <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs md:col-span-2">
                <strong className="block text-[10px] uppercase text-sky-400 font-bold tracking-wider mb-1">Oral Motor Mechanism & Voice</strong>
                <p className="text-slate-100 font-normal">{data.oral_motor_voice}</p>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Section 4: Diagnostic Impressions & Medical Necessity */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <ShieldCheck className="w-3.5 h-3.5 text-sky-400" />
            <span>4. Diagnostic Impressions & Medicaid Medical Necessity</span>
          </div>
          <div className="space-y-3 leading-relaxed">
            {diagnosticImpressions ? (
            <div>
              <strong className="block text-[10px] uppercase font-bold text-sky-400 mb-1">Clinical Impressions:</strong>
              <p className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors text-slate-100 leading-relaxed shadow-xs">{diagnosticImpressions}</p>
            </div>
            ) : null}
            {medicalNecessityRationale ? (
            <div>
              <strong className="text-amber-400 block text-[10px] uppercase font-bold tracking-wider mb-1">Medicaid Skilled Need Rationale:</strong>
              <p className="p-3.5 bg-gradient-to-b from-[#18140e] to-[#0f0c08] rounded-xl border border-amber-500/35 text-amber-100 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(245,158,11,0.1)] italic leading-relaxed">{medicalNecessityRationale}</p>
            </div>
            ) : null}
          </div>
        </div>

        {/* Section 5: Initial Canonical Treatment Goals */}
        {data.established_goals && data.established_goals.length > 0 && (
          <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
            <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
              <Target className="w-3.5 h-3.5 text-emerald-400" />
              <span>5. Established Treatment Goals (Plan of Care)</span>
            </div>
            <div className="space-y-2.5">
              {data.established_goals.map((g, idx) => (
                <div key={idx} className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors space-y-1.5 shadow-xs">
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-white text-xs">{goalTermLabel(g) ? `${goalTermLabel(g)} goal` : 'Goal'} {idx + 1}: {g.description}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-500/40 font-bold ml-2 shrink-0">
                      Target: {g.target_percentage || 80}%
                    </span>
                  </div>
                  {g.baseline && (
                    <div className="text-[11px] text-slate-300">
                      <span className="font-medium text-slate-400">Baseline Performance:</span> {g.baseline}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 6: Home Program: Strategies, Progress & Updates */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <HeartHandshake className="w-3.5 h-3.5 text-sky-400" />
            <span>6. Home Program: Strategies, Progress & Updates</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200 leading-relaxed">
            {homeProgramStrategies ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase font-bold text-sky-400 tracking-wider mb-1">Environmental Carryover Strategies:</strong>
              <p className="text-slate-100 font-normal">{homeProgramStrategies}</p>
            </div>
            ) : null}
            {caregiverTrainingPlan ? (
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase font-bold text-sky-400 tracking-wider mb-1">Direct Caregiver Coaching Plan:</strong>
              <p className="text-slate-100 font-normal">{caregiverTrainingPlan}</p>
            </div>
            ) : null}
          </div>
        </div>

        {/* Section 7: Discharge Criteria & Ordering Physician */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200">
          {dischargeCriteria ? (
          <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400 block mb-1">Discharge Criteria</span>
            <p className="text-slate-100 leading-relaxed font-normal">{dischargeCriteria}</p>
          </div>
          ) : null}
          <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Ordering Physician</span>
            <p className="text-white font-bold">{data.physician_name || 'Physician on file'}</p>
            <div className="text-[11px] text-slate-400 flex flex-wrap gap-2 mt-1 font-mono">
              {data.physician_npi && <span>NPI: {data.physician_npi}</span>}
              {data.physician_fax && <span>Fax: {data.physician_fax}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthoredPlanDoc(doc.doc_type)) {
    const data = sectionData as PocSectionData;
    return (
      <div className={`space-y-4 text-xs ${lightTheme ? 'clinical-structured-light text-slate-800' : ''}`}>
        {/* POC Header Highlights */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#1c2c47] via-[#162339] to-[#121c2e] border border-slate-600/70 shadow-[0_4px_16px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)] grid grid-cols-2 sm:grid-cols-4 gap-3 text-slate-200">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Certification Period</span>
            <span className="font-mono text-white font-bold">{data.poc_start_date || doc.poc_start_date} to {data.poc_end_date || doc.poc_end_date}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Treatment Frequency</span>
            <span className="font-bold text-white">{data.frequency || '2x/week'}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Duration & Setting</span>
            <span className="font-bold text-white">{data.duration || '60 min'} • {data.setting || 'Home'} • CPT {isFeedingPlanDoc(doc.doc_type) ? '92526' : (data.cpt_code || '92507')}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Prognosis</span>
            <span className="font-bold text-emerald-400">{data.prognosis || 'Good'}</span>
          </div>
        </div>

        {/* Section 1: Progress Summary & Continued Need */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <Activity className="w-3.5 h-3.5 text-indigo-400" />
            <span>1. Progress Summary & Continued Skilled Need</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200 leading-relaxed">
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-indigo-300 font-bold tracking-wider mb-1">Prior Cycle Progress:</strong>
              <p className="text-slate-100 font-normal">{data.progress_summary || 'Progress demonstrated during prior certification window.'}</p>
            </div>
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-indigo-300 font-bold tracking-wider mb-1">Caregiver Participation:</strong>
              <p className="text-slate-100 font-normal">{data.caregiver_participation || 'Caregiver actively engaged in therapy sessions and home practice.'}</p>
            </div>
            <div className="p-3.5 bg-gradient-to-b from-[#18140e] to-[#0f0c08] rounded-xl border border-amber-500/35 md:col-span-2 text-amber-100 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(245,158,11,0.1)] italic leading-relaxed">
              <strong className="block text-[10px] uppercase text-amber-400 font-bold tracking-wider not-italic mb-1">Continued Skilled Need Justification:</strong>
              <p>{continuedSkilledNeedDisplay(doc.doc_type, data.continued_skilled_need)}</p>
            </div>
          </div>
        </div>

        {/* Section 2: Plan of Care Goals */}
        {data.goals && data.goals.length > 0 && (
          <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
            <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
              <Target className="w-3.5 h-3.5 text-indigo-400" />
              <span>2. Targeted Goals for this Plan of Care Cycle</span>
            </div>
            <div className="space-y-2.5">
              {data.goals.map((g, idx) => (
                <div key={idx} className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors space-y-1.5 shadow-xs">
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-white text-xs">{goalTermLabel(g) ? `${goalTermLabel(g)} goal` : 'Goal'} {idx + 1}: {g.description}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-950/60 text-indigo-300 border border-indigo-500/40 font-bold ml-2 shrink-0">
                      Target: {g.target_percentage || 80}%
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-300 font-mono">
                    {g.baseline && <span>Baseline: {g.baseline}</span>}
                    {g.current_percentage !== undefined && (
                      <span className="text-sky-400 font-bold">Current: {g.current_percentage}%</span>
                    )}
                    {g.status && (
                      <span className="uppercase text-slate-400 font-bold">Status: {g.status}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 3: Home Carryover & Discharge Plan */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200">
          <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
            <strong className="block text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1">Home Program Carryover Plan:</strong>
            <p className="text-slate-100 leading-relaxed font-normal whitespace-pre-line">{data.home_program_carryover || 'Daily functional carryover routines implemented with caregiver.'}</p>
          </div>
          <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
            <strong className="block text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1">Anticipated Discharge Plan:</strong>
            <p className="text-slate-100 leading-relaxed font-normal">{data.discharge_plan || 'Discharge anticipated upon achievement of 80% mastery.'}</p>
          </div>
        </div>

        {/* Section 4: Physician Certification Information */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] flex flex-wrap items-center justify-between gap-3 text-slate-200">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Ordering Certifying Physician</span>
            <span className="font-bold text-white text-sm">{data.physician_name || doc.physician_name || 'Physician on file'}</span>
            <div className="text-[11px] text-slate-400 font-mono flex items-center space-x-3 mt-1">
              {(data.physician_npi || doc.physician_npi) && <span>NPI: {data.physician_npi || doc.physician_npi}</span>}
              {(data.physician_fax || doc.physician_fax) && <span>Fax: {data.physician_fax || doc.physician_fax}</span>}
            </div>
          </div>
          <div className="text-right">
            <span className="px-2.5 py-1 rounded bg-indigo-950/60 border border-indigo-500/40 text-indigo-300 font-mono text-[10px] font-bold uppercase">
              Physician Signature via Faxage
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (doc.doc_type === 'QPR') {
    const data = sectionData as QprSectionData;
    return (
      <div className={`space-y-4 text-xs ${lightTheme ? 'clinical-structured-light text-slate-800' : ''}`}>
        {/* QPR Header Highlights */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#1c2c47] via-[#162339] to-[#121c2e] border border-slate-600/70 shadow-[0_4px_16px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)] grid grid-cols-2 sm:grid-cols-4 gap-3 text-slate-200">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 block">Reporting Period</span>
            <span className="font-bold text-white">{qprAbsentText(data.reporting_period_label || doc.qpr_quarter)}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 block">Sessions Completed</span>
            <span className="font-bold text-emerald-400 text-sm">{typeof data.attendance?.completed_sessions === 'number' ? `${data.attendance.completed_sessions} Sessions` : 'Not documented'}</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 block">Cancellations & No-Shows</span>
            <span className="font-mono text-slate-300">{typeof data.attendance?.cancelled_sessions === 'number' ? data.attendance.cancelled_sessions : 'Not documented'} Cancelled • {typeof data.attendance?.no_show_sessions === 'number' ? data.attendance.no_show_sessions : 'Not documented'} No-Show</span>
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 block">Recommended Frequency</span>
            <span className="font-bold text-white">{qprAbsentText(data.recommended_frequency)}</span>
          </div>
        </div>

        {/* Section 1: Collaboration & Clinical Summary */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <Activity className="w-3.5 h-3.5 text-purple-400" />
            <span>1. Clinical Progress & Interprofessional Collaboration</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-200 leading-relaxed">
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-purple-300 font-bold tracking-wider mb-1">Collaboration with Team & Coordinator:</strong>
              <p className="text-slate-100 font-normal">{qprAbsentText(data.collaboration_summary)}</p>
            </div>
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-purple-300 font-bold tracking-wider mb-1">Medical / Medication Updates:</strong>
              <p className="text-slate-100 font-normal">{qprAbsentText(data.medical_updates)}</p>
            </div>
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs md:col-span-2">
              <strong className="block text-[10px] uppercase text-purple-300 font-bold tracking-wider mb-1">Therapy Clinical Summary:</strong>
              <p className="text-slate-100 font-normal">{qprAbsentText(data.therapy_summary)}</p>
            </div>
            <div className="p-3.5 bg-gradient-to-b from-[#18140e] to-[#0f0c08] rounded-xl border border-amber-500/35 md:col-span-2 text-amber-100 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(245,158,11,0.1)] italic leading-relaxed">
              <strong className="block text-[10px] uppercase font-bold tracking-wider not-italic text-amber-400 mb-1">Skilled Care Justification:</strong>
              <p>{qprAbsentText(data.skilled_care_justification)}</p>
            </div>
          </div>
        </div>

        {/* Section 2: Goal-by-Goal Review */}
        {data.goals_review && data.goals_review.length > 0 && (
          <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
            <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
              <Target className="w-3.5 h-3.5 text-purple-400" />
              <span>2. Goal-by-Goal Progress against Canonical Chart Objectives</span>
            </div>
            <div className="space-y-3">
              {data.goals_review.map((item, idx) => {
                const view = qprGoalReviewPresentation(item);
                return (
                <div key={idx} className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors space-y-2 shadow-xs">
                  <div className="flex flex-wrap justify-between items-start gap-2">
                    <span className="font-bold text-white text-xs">Goal {idx + 1}: {item.description}</span>
                    <div className="flex items-center space-x-2 shrink-0">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase ${
                        item.change_status === 'IMPROVED' ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/40' :
                        item.change_status === 'MAINTAINED' ? 'bg-sky-950/60 text-sky-300 border border-sky-500/40' :
                        item.change_status === 'REGRESSED' ? 'bg-amber-950/60 text-amber-300 border border-amber-500/40' :
                        'bg-slate-800 text-slate-300 border border-slate-600'
                      }`}>
                        {view.changeStatus}
                      </span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-950/60 text-purple-300 border border-purple-500/40 font-bold">
                        {view.recommendation}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-300 font-mono">
                    <div>Baseline: <span className="text-slate-100 font-bold">{view.baseline}</span></div>
                    <div>Target: <span className="text-emerald-400 font-bold">{view.target}</span></div>
                    <div>Current Accuracy: <span className="text-sky-400 font-bold">{view.currentAccuracy}</span></div>
                    <div>Cueing: <span className="text-slate-200">{view.cueing}</span></div>
                  </div>
                  {item.functional_impact && (
                    <div className="text-[11px] text-slate-300">
                      <span className="font-medium text-slate-400">Functional Impact:</span> {item.functional_impact}
                    </div>
                  )}
                  {item.clinical_rationale && (
                    <div className="text-[11px] text-slate-300">
                      <span className="font-medium text-slate-400">Clinical Rationale:</span> {item.clinical_rationale}
                    </div>
                  )}
                </div>
              );})}
            </div>
          </div>
        )}

        {/* Section 3: Home Program: Strategies, Progress & Updates */}
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-3">
          <div className="text-[11px] font-bold text-slate-200 uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-700/80 pb-2">
            <HeartHandshake className="w-3.5 h-3.5 text-purple-400" />
            <span>3. Home Program: Strategies, Progress & Updates</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-slate-200 leading-relaxed">
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-purple-300 font-bold tracking-wider mb-1">Carryover Strategies:</strong>
              <p className="text-slate-100 font-normal">{qprAbsentText(data.home_program_strategies)}</p>
            </div>
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-purple-300 font-bold tracking-wider mb-1">Caregiver Implementation:</strong>
              <span className="inline-block mb-1 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-500/40">
                {data.home_program_oversight?.caregiver_implementation || 'Not assessed'}
              </span>
              <p className="text-slate-100 font-normal">{qprAbsentText(data.home_program_oversight?.clinician_oversight_notes)}</p>
            </div>
            <div className="p-3.5 bg-[#0a101b] rounded-xl border border-slate-700/80 hover:border-slate-600/80 transition-colors shadow-xs">
              <strong className="block text-[10px] uppercase text-purple-300 font-bold tracking-wider mb-1">Adjustments for Next Quarter:</strong>
              <p className="text-slate-100 font-normal">{qprAbsentText(data.home_program_changes)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
